import React, { useEffect, useState, ReactNode, createContext } from 'react';
import ReactDOM from 'react-dom';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import weakMemoize from '@emotion/weak-memoize';
import { preloadAllCardImages } from './SubComponents/Card';
import { consumeDetachedWindow, consumeAnyReservedWindow, isWindowTransferring, clearWindowTransferring, unregisterActiveWindow, registerActiveWindow } from '../../Utils/DetachedWindowManager';
import { windowFocusManager } from '../../Utils/WindowFocusManager';
import { isPopupDebugEnabled } from '../../Utils/PopupDebug';
import { reportDetachedIssue } from '../../Utils/DetachedIssueLogger';

export const DetachedWindowContext = createContext<Window | null>(null);

// Create a memoized function to create Emotion cache for the detached window
const memoizedCreateCacheWithContainer = weakMemoize((container: HTMLElement) => {
  return createCache({ 
    key: 'detached-window-emotion-cache',
    container 
  });
});
const IS_DEV = import.meta.env.DEV;
const getDetachedWindowTitle = (title: string): string => `PokerSolutions - ${title}`;
const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (character) => {
  switch (character) {
    case '&':
      return '&amp;';
    case '<':
      return '&lt;';
    case '>':
      return '&gt;';
    case '"':
      return '&quot;';
    case "'":
      return '&#39;';
    default:
      return character;
  }
});

interface WindowPortalProps {
  children: ReactNode;
  title?: string;
  width?: number;
  height?: number;
  loadingLabel?: string;
  onClose?: () => void;
  onPopupBlocked?: () => void;
  roomId?: string;
  tileable?: boolean;
}

const WindowPortal: React.FC<WindowPortalProps> = ({
  children,
  title = 'Poker Room',
  width = 1024,
  height = 640,
  loadingLabel = 'Loading poker room...',
  onClose,
  onPopupBlocked,
  roomId,
  tileable = true,
}) => {
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  const [windowInstance, setWindowInstance] = useState<Window | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [headElement, setHeadElement] = useState<HTMLElement | null>(null);
  const windowTitle = getDetachedWindowTitle(title);

  useEffect(() => {
    let didUnmount = false;
    let cleanupFn: (() => void) | null = null;

    // Open or reuse a window (reuse if reserved on user click)
    const windowFeatures = `width=${width},height=${height},menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no`;
    const reserved = (typeof roomId === 'string' && roomId) ? consumeDetachedWindow(roomId) : (consumeAnyReservedWindow());

    const openAndInit = (newWindow: Window) => {
      if (didUnmount) {
        try { newWindow.close(); } catch {}
        return;
      }

      if (isPopupDebugEnabled()) {
        console.debug('[detached] WindowPortal opened', {
          roomId,
          title,
          usedReserved: Boolean(reserved && reserved === newWindow),
        });
      }

      newWindow.document.title = windowTitle;
    
      // Create basic HTML structure with data-detached attribute for styling
      newWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${escapeHtml(windowTitle)}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            html, body {
              margin: 0;
              padding: 0;
              background-color: #0F1015;
              overflow: hidden;
              width: 100%;
              height: 100%;
            }
            
            .outer-container {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              overflow: hidden;
              width: 100%;
              height: 100%;
            }
            
            #portal-root {
              width: 100%;
              height: 100%;
              background-color: #191A1F;
              position: relative;
              overflow: auto;
              box-shadow: none;
            }
            
            .loading-indicator {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              background-color: #191A1F;
              color: white;
              font-family: Arial, sans-serif;
              z-index: 9999;
            }
            
            .loading-spinner {
              border: 5px solid rgba(255, 255, 255, 0.3);
              border-radius: 50%;
              border-top: 5px solid #ffffff;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin-bottom: 15px;
            }
            
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body data-detached="true">
          <div class="outer-container">
            <div id="portal-root">
              <div class="loading-indicator" id="loading-indicator">
                <div class="loading-spinner"></div>
                <div>${loadingLabel}</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    
      // Close the document to finish writing
      newWindow.document.close();
    
      const containerElement = newWindow.document.getElementById('portal-root');
      const headElement = newWindow.document.head;
      const loadingIndicator = newWindow.document.getElementById('loading-indicator');
    
      // Store references
      if (!didUnmount) {
        setContainerEl(containerElement);
        setWindowInstance(newWindow);
        setHeadElement(headElement);
      }
    
      let registerElectronWindow: (() => void) | null = null;

      // Register window with focus manager if roomId is provided
      if (roomId) {
        windowFocusManager.registerWindow(roomId, newWindow, title, { tileable });
        registerActiveWindow(roomId, newWindow);
      
        registerElectronWindow = () => {
          if (typeof (newWindow as any).hs?.registerWindowRoomId === 'function') {
            (newWindow as any).hs.registerWindowRoomId(roomId);
          }
        };

        // In Electron, register from the child window context. Call immediately as well as on load,
        // because this portal writes into about:blank and the load event can fire before this listener exists.
        registerElectronWindow();
        newWindow.addEventListener('load', registerElectronWindow);
      }

      const externalStyleMap = new Map<string, HTMLLinkElement>();
      const inlineStyleMap = new Map<HTMLStyleElement, HTMLStyleElement>();

      const copyAllStyles = () => {
        try {
          const targetHead = newWindow.document.head;
          const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
          links.forEach(link => {
            const href = link.getAttribute('href');
            if (!href) {
              return;
            }
            if (externalStyleMap.has(href)) {
              return;
            }
            const newLink = newWindow.document.createElement('link');
            newLink.rel = 'stylesheet';
            newLink.href = href;
            if (link.getAttribute('crossorigin')) {
              newLink.setAttribute('crossorigin', link.getAttribute('crossorigin') || '');
            }
            externalStyleMap.set(href, newLink);
            targetHead.appendChild(newLink);
          });

          const styles = Array.from(document.querySelectorAll('style'));
          styles.forEach(style => {
            const mapped = inlineStyleMap.get(style as HTMLStyleElement);
            if (mapped) {
              mapped.textContent = style.textContent;
            } else {
              const newStyle = newWindow.document.createElement('style');
              if (style.hasAttribute('data-emotion')) {
                newStyle.setAttribute('data-emotion', style.getAttribute('data-emotion') || '');
              }
              newStyle.textContent = style.textContent;
              inlineStyleMap.set(style as HTMLStyleElement, newStyle);
              targetHead.appendChild(newStyle);
            }
          });
        } catch (error) {
          console.error('Error copying styles:', error);
        }
      };

      copyAllStyles();
    
      // Preload card images using the existing function
      const loadCardImages = async () => {
        try {
          // Use the existing preload function
          await preloadAllCardImages();
        
          // Remove loading indicator
          if (loadingIndicator) {
            loadingIndicator.style.display = 'none';
          }
        
        } catch (error) {
          console.error('Error preloading card images in detached window:', error);
        
          // Even on error, hide the loader after a timeout
          if (loadingIndicator) {
            loadingIndicator.style.display = 'none';
          }
        }
      };
    
      // Start preloading card images
      loadCardImages();
    
      // Set a maximum time to show the loader (3 seconds)
      const timeoutId = setTimeout(() => {
        if (loadingIndicator) {
          loadingIndicator.style.display = 'none';
        }
      }, 3000);
    
      const styleObserver = IS_DEV ? new MutationObserver(() => copyAllStyles()) : null;
    
      if (styleObserver) {
        styleObserver.observe(document.head, { 
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-emotion']
        });
      }
    
      // Set ready state immediately
      if (!didUnmount) {
        setIsReady(true);
      }
    
      // Clear transferring flag if this window was transferred (prevents it from being closed on next unmount)
      if (reserved && isWindowTransferring(reserved)) {
        clearWindowTransferring(reserved);
        if (isPopupDebugEnabled()) {
          console.debug('[detached] Cleared transferring flag', { roomId, title });
        }
      }

      // Handle window close
      const handleWindowClose = () => {
        // Unregister window from focus manager
        if (roomId) {
          windowFocusManager.unregisterWindow(roomId);
        }

        if (onClose) {
          onClose();
        }
      };
    
      // Listen for both beforeunload and unload to ensure we catch all close events
      newWindow.addEventListener('beforeunload', handleWindowClose);
      newWindow.addEventListener('unload', handleWindowClose);

      cleanupFn = () => {
        clearTimeout(timeoutId);
        if (styleObserver) {
          styleObserver.disconnect();
        }
        // Always unregister this roomId mapping on unmount to avoid stale focus entries,
        // including during tournament table transfers (same Window reused with a new roomId).
        if (roomId) {
          windowFocusManager.unregisterWindow(roomId);
        }
        newWindow.removeEventListener('beforeunload', handleWindowClose);
        newWindow.removeEventListener('unload', handleWindowClose);
        if (registerElectronWindow) {
          newWindow.removeEventListener('load', registerElectronWindow);
        }

        // Only close the window if it's not being transferred to another room
        if (!isWindowTransferring(newWindow)) {
          if (isPopupDebugEnabled()) {
            console.debug('[detached] Closing detached window', { roomId, title });
          }
          newWindow.close();
          if (roomId) {
            unregisterActiveWindow(roomId); // Clean up tracking
          }
        }
      };
    };

    const openNewWindowOrReportBlocked = () => {
      const newWindow = window.open('', '', windowFeatures);
        if (!newWindow) {
          const activation = (navigator as any)?.userActivation;
          reportDetachedIssue('popup_blocked', {
            roomId,
            title,
            action: 'main_view_after_popup_blocked',
            hadReserved: Boolean(reserved),
            reservedWasClosed: reserved ? reserved.closed : undefined,
            userActivation: activation
              ? { isActive: Boolean(activation.isActive), hasBeenActive: Boolean(activation.hasBeenActive) }
              : undefined,
        });
        onPopupBlocked?.();
        return;
      }
      openAndInit(newWindow);
    };

    if (reserved && !reserved.closed) {
      openAndInit(reserved);
    } else {
      openNewWindowOrReportBlocked();
    }

    return () => {
      didUnmount = true;
      cleanupFn?.();
    };
  }, []);

  useEffect(() => {
    if (!windowInstance || windowInstance.closed) return;

    try {
      windowInstance.document.title = windowTitle;
      const titleElement = windowInstance.document.querySelector('title');
      if (titleElement) {
        titleElement.textContent = windowTitle;
      }
    } catch {
      // ignore title update failures from closed or restricted windows
    }

    if (roomId) {
      windowFocusManager.registerWindow(roomId, windowInstance, title, { tileable });
    }
  }, [roomId, tileable, title, windowInstance, windowTitle]);

  // Only render once we have a container element and window is ready
  if (!containerEl || !windowInstance || !isReady || !headElement) {
    return null;
  }

  // Use createPortal to render children into the new window, providing the window instance via context
  // Wrap with CacheProvider to ensure Emotion styles are injected into the detached window
  return (
    <DetachedWindowContext.Provider value={windowInstance}>
      {ReactDOM.createPortal(
        <CacheProvider value={memoizedCreateCacheWithContainer(headElement)}>
          {children}
        </CacheProvider>,
        containerEl
      )}
    </DetachedWindowContext.Provider>
  );
};

export default WindowPortal;
